/**
 * SEO head helpers for TanStack Router routes.
 *
 * Output is the `{ meta, links }` shape that TanStack Router's `head()`
 * function expects. The library is dependency-free and framework-neutral
 * beyond that — any router that consumes the same shape will work.
 *
 * Usage:
 *
 *   // app/lib/seo.ts (per project)
 *   import { createPageHead } from 'drf-react/seo/seo'
 *
 *   export const pageHead = createPageHead({
 *     siteUrl: 'https://example.com',
 *     titleSuffix: 'Example',
 *     homePageTitle: 'Example | Home',
 *   })
 *
 *   // app/routes/about.tsx
 *   export const Route = createFileRoute('/about')({
 *     head: () => pageHead({
 *       path: '/about',
 *       title: 'About',
 *       description: 'About Example',
 *     }),
 *     component: AboutPage,
 *   })
 *
 * Why this exists: TanStack Router dedupes meta tags by `name`/`property`
 * but does NOT dedupe `<link>` tags by `rel`. Setting a canonical link on
 * the root route therefore produces duplicate canonical tags on every
 * child page. This helper sidesteps that by always emitting the canonical
 * at the route level (the home page included) and never on the root.
 */

export type MetaTag = Record<string, string>
export type LinkTag = Record<string, string>

export type PageHead = {
  meta: MetaTag[]
  links: LinkTag[]
}

export type PageHeadOptions = {
  /**
   * The path for this page (e.g. `/`, `/about`, `/blog/my-post`).
   * Used to generate the canonical URL and `og:url`.
   */
  path: string
  /**
   * The page title. The configured `titleSuffix` is appended automatically
   * (e.g. `"About"` becomes `"About — Example"`). Use `rawTitle` to opt out.
   */
  title?: string
  /**
   * Use this instead of `title` when you want full control over the title
   * (no suffix appended). Useful for the home page.
   */
  rawTitle?: string
  /**
   * Meta description for the page. Drives `description`, `og:description`,
   * and `twitter:description` when provided.
   */
  description?: string
  /**
   * Open Graph type. Defaults to `'website'`. Use `'article'` for blog posts.
   */
  ogType?: string
}

export type CreatePageHeadConfig = {
  /**
   * Canonical site origin, no trailing slash. Example: `'https://example.com'`.
   */
  siteUrl: string
  /**
   * String appended to per-page `title` values. Example: with
   * `titleSuffix: 'Example'` and `separator: ' — '`, `pageHead({ title: 'About' })`
   * produces a `<title>About — Example</title>`.
   */
  titleSuffix?: string
  /**
   * Title used when no `title` or `rawTitle` is supplied. Typically the
   * home-page title. If omitted, `titleSuffix` is used.
   */
  homePageTitle?: string
  /**
   * Separator between page title and suffix. Defaults to ` — ` (em dash).
   */
  separator?: string
}

/**
 * Build a project-bound `pageHead()` helper.
 */
export function createPageHead(config: CreatePageHeadConfig) {
  const {
    siteUrl,
    titleSuffix,
    homePageTitle,
    separator = ' — '
  } = config

  const formatTitle = (pageName?: string): string => {
    if (!pageName) return homePageTitle ?? titleSuffix ?? ''
    if (!titleSuffix) return pageName
    return `${pageName}${separator}${titleSuffix}`
  }

  return function pageHead(opts: PageHeadOptions): PageHead {
    const { path, title, rawTitle, description, ogType = 'website' } = opts
    const fullTitle = rawTitle ?? formatTitle(title)
    const canonicalUrl = `${siteUrl}${path}`

    const meta: MetaTag[] = [
      { title: fullTitle },
      { property: 'og:type', content: ogType },
      { property: 'og:title', content: fullTitle },
      { property: 'og:url', content: canonicalUrl },
      { name: 'twitter:title', content: fullTitle }
    ]

    if (description) {
      meta.push(
        { name: 'description', content: description },
        { property: 'og:description', content: description },
        { name: 'twitter:description', content: description }
      )
    }

    return {
      meta,
      links: [{ rel: 'canonical', href: canonicalUrl }]
    }
  }
}
